import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { startOfMonth, endOfMonth, format } from 'date-fns';

import { State, OrganizationModel, Version, SelectOption } from 'models';
import { BalanceType } from 'utils/enums';
import { getCurrentCooperative } from 'store/home/selectors';
import { toDate, serviceDateFormat } from 'utils';
import services from 'services';

import { InvestotrBalanceDetails } from 'components';

interface InvestorBalanceState {
  version: Version;
  startDate: Date | null;
  endDate: Date | null;
  loading: boolean;
  error: boolean;
  balanceType: SelectOption<BalanceType>;
}

function buildURL(
  coopId: string,
  version: Version,
  startDate: Date,
  endDate: Date,
  balanceType: BalanceType
) {
  const searchParams = `startDate=${format(
    startDate,
    serviceDateFormat
  )}&endDate=${format(endDate, serviceDateFormat)}&list=${
    version.value
  }&del=${balanceType}`;

  return `/api/portfolios/${coopId}/balance?${searchParams}`;
}

const InvestorBalance = () => {
  const { coopId, coopName } = useSelector((state: State) => ({
    coopId: state.settings.current_cooperative,
    coopName: getCurrentCooperative(state)?.Name || null,
  }));

  const [state, setState] = useState<InvestorBalanceState>({
    version: {
      label: '#portal.investor.report.balance.list.collapse',
      value: 1,
    },
    startDate: null,
    endDate: null,
    loading: false,
    error: true,
    balanceType: { label: 'Kaikki', value: BalanceType.All },
  });

  const fetchOrganization = async (coopId: string) => {
    const organization: OrganizationModel = (
      await services.get(`/api/portfolios/${coopId}/organization`)
    ).data.Data;

    return organization;
  };

  useEffect(() => {
    (async function () {
      if (coopId) {
        try {
          setState((prevState) => ({
            ...prevState,
            loading: true,
            error: false,
          }));

          const organization = await fetchOrganization(coopId);
          const d = new Date();
          const endingDate = toDate(organization.PeriodEndingDate);

          setState((prevState) => ({
            ...prevState,
            startDate: endingDate ? startOfMonth(endingDate) : startOfMonth(d),
            endDate: endingDate ? endOfMonth(endingDate) : endOfMonth(d),
            loading: false,
          }));
        } catch (err) {
          console.error(err);

          setState((prevState) => ({
            ...prevState,
            loading: false,
            error: true,
          }));
        }
      }
    })();
  }, [coopId]);

  const handleChangeDate = useCallback((date: Date | null, name?: string) => {
    if (name) {
      setState((prevState) => ({
        ...prevState,
        [name]: date,
      }));
    }
  }, []);

  const handleChangeBalanceType = useCallback(
    (option: SelectOption<BalanceType>) => {
      setState((prevState) => ({
        ...prevState,
        balanceType: option,
      }));
    },
    []
  );

  const handleChangeVersion = useCallback((version: Version) => {
    setState((prevState) => ({
      ...prevState,
      version,
    }));
  }, []);

  const url = useMemo(() => {
    if (coopId && state.startDate && state.endDate) {
      return buildURL(
        coopId,
        state.version,
        state.startDate,
        state.endDate,
        state.balanceType.value
      );
    }

    return '';
  }, [
    coopId,
    state.version,
    state.startDate,
    state.endDate,
    state.balanceType,
  ]);

  const fileName = useMemo(() => {
    return `investor_balance__${coopName}`;
  }, [coopName]);

  return (
    <InvestotrBalanceDetails
      {...state}
      url={url}
      fileName={fileName}
      handleChangeDate={handleChangeDate}
      handleChangeVersion={handleChangeVersion}
      handleChangeBalanceType={handleChangeBalanceType}
    />
  );
};

export default InvestorBalance;
